import { Input } from "@/components/ui/input"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { FromSettings } from "@/features/settings/components/FromSettings"
import { strayPlaceholders } from "@/features/settings/pages/material-compliance/supplier-inquiries/templateChecks"
import { needsWriting } from "@/features/settings/setup/placeholders"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Settings } from "lucide-react"
import { useRef, type DragEvent } from "react"
import { useEmailSenderIdentity } from "../../../../api/inquiryEmailApi"
import {
  DEFAULT_SCOPE_WARN_SUBJECTS,
  useTemplateSettings,
} from "../../../../api/templateSettingsApi"
import { McPill } from "../../../shared/McPill"
import { SectionLabel } from "../../../shared/SectionLabel"
import type { DraftArticle } from "./AddArticleDialog"
import { ArticlesEditor } from "./ArticlesEditor"
import { AttachmentLanguageSection } from "./AttachmentLanguageSection"
import { CopyButton } from "./CopyButton"
import { DeadlineDeliverySection } from "./DeadlineDeliverySection"
import type { DraftSettingsTarget } from "./DraftSettingsSheet"
import type {
  AttachmentFormat,
  InquiryDraftItem,
  Lang,
  Stage,
} from "./inquiryDraftMock"
import {
  INQUIRY_VAR_TOOLTIP_KEYS,
  INQUIRY_VARS_BY_LANG,
} from "./inquiryDraftMock"
import {
  InquiryRegulationsNote,
  type RegulationEditor,
} from "./InquiryRegulationsNote"
import { RecipientEditor } from "./RecipientEditor"
import { RecipientList } from "./RecipientList"
import { RichTextArea, RichTextInput } from "./RichTextArea"

interface DraftComposerPanelProps {
  bulk: boolean
  inquiries: InquiryDraftItem[]
  primary: InquiryDraftItem
  frameworks: string[]
  stage: Stage
  language: Lang
  to: string[]
  cc: string[]
  setTo: (v: string[]) => void
  setCc: (v: string[]) => void
  subject: string
  setSubject: (v: string) => void
  body: string
  setBody: (v: string | ((prev: string) => string)) => void
  articles: DraftArticle[]
  setArticles: (v: DraftArticle[]) => void
  due: string
  setDue: (v: string) => void
  // Attachment format + questionnaire language: single-valued and batch-wide, owned by
  // InquiryDraftPage so the header's language tabs and this section share one state.
  format: AttachmentFormat
  setFormat: (v: AttachmentFormat) => void
  setLanguage: (v: Lang) => void
  onBulkSupplierEmailAdded?: (inquiryId: string, email: string) => void
  onBulkRecipientsChanged?: (
    inquiryId: string,
    field: "recipientsTo" | "recipientsCc",
    next: string[],
    address: string,
  ) => void
  // "<inquiryId>:<field>" -> the address currently mid-PATCH for it — shows a
  // spinner in RecipientList on that chip (removal) or its add control (add).
  pendingBulkRecipientAddresses?: Map<string, string>
  // Opens the org's signature settings — the actual edit surface lives in the page
  // header's gear (next to the language switcher), not here; this is a discoverability
  // link so a user looking at the body box isn't left wondering where the signature
  // below it comes from.
  onEditSignature: () => void
  // Opens one of the org's settings beside the draft (the page's side sheet), from the
  // "Set in Settings" links next to the values that come from them.
  onOpenSettings?: (target: DraftSettingsTarget) => void
  // The draft's regulation picker (TUR-1730); absent while regulation settings are off.
  regulationEditor?: RegulationEditor
  /** The draft the regulation picker edits: the batch's first draft, which in a
   *  partly sent batch is not the primary (a sent one is frozen). */
  regulationDraft?: InquiryDraftItem
}

/** Where the email comes from, and who every new inquiry copies: both set in
 *  Settings, so said here with the way to change them. */
function SenderAndDefaultCc({
  onOpenSettings,
}: {
  onOpenSettings?: (target: DraftSettingsTarget) => void
}) {
  const { translate } = useTranslation()
  const { data: identity } = useEmailSenderIdentity()
  const { data: settings } = useTemplateSettings()
  const defaultCc = settings?.defaultCc ?? []
  const open = (target: DraftSettingsTarget) =>
    onOpenSettings && (() => onOpenSettings(target))

  return (
    <div className="flex flex-col gap-1 text-[11.5px] text-grey5">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {identity?.fullAddress
          ? translate("MC_INQ_DRAFT_FROM", { address: identity.fullAddress })
          : translate("MC_INQ_DRAFT_FROM_NONE")}
        <FromSettings
          target="email.sendingAddress"
          onOpen={open("email.sendingAddress")}
        />
      </span>
      {defaultCc.length > 0 && (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {translate("MC_INQ_DRAFT_DEFAULT_CC", {
            addresses: defaultCc.join(", "),
          })}
          <FromSettings target="inquiry.cc" onOpen={open("inquiry.cc")} />
        </span>
      )}
    </div>
  )
}

export function DraftComposerPanel(props: DraftComposerPanelProps) {
  const { translate } = useTranslation()
  const {
    bulk,
    inquiries,
    primary,
    stage,
    language,
    to,
    cc,
    setTo,
    setCc,
    subject,
    setSubject,
    body,
    setBody,
    articles,
    setArticles,
    due,
    setDue,
    format,
    setFormat,
    setLanguage,
    onBulkSupplierEmailAdded,
    onBulkRecipientsChanged,
    pendingBulkRecipientAddresses,
    onEditSignature,
    onOpenSettings,
    regulationEditor,
    regulationDraft: editedRegulationDraft,
  } = props

  const bodyRef = useRef<HTMLTextAreaElement>(null)
  // A typo'd variable ("{referenz}}") is sent verbatim; the backend refuses the send.
  const strayTokens = strayPlaceholders(
    stage === "reminder" ? [body] : [subject, body],
  )
  // The org signature (from template settings) is appended below the body on send. Shown
  // read-only here so the user sees the full email and understands it comes from settings.
  // Editing it opens the same settings from the page header's gear, next to the language
  // switcher — not from here, so it stays reachable on the reminder stage too.
  const { data: templateSettings } = useTemplateSettings()
  const signature = templateSettings?.emailSignatures?.[language]

  // The backend-owned reference tag appended to every subject on send. Shown read-only so
  // the composer reflects the full subject that actually goes out (#2). The reference is
  // assigned on send, so a not-yet-sent draft shows a "pending" placeholder.
  // The regulations note speaks for the draft its picker edits, else the primary.
  const regulationDraft = editedRegulationDraft ?? primary
  const refTag = primary.reference
    ? `[Ref: ${primary.reference}]`
    : translate("MC_INQ_DRAFT_SUBJECT_REF_PENDING")
  // A reminder always sends as a threaded reply to the original request: the backend forces
  // the subject to "Re: <the exact subject that was sent>" (variables resolved, reference
  // token included) — editing it would fork a new mail thread. The composer only has the raw,
  // unresolved template here, NOT the sent subject, so show a neutral read-only placeholder
  // rather than a token-derived line that wouldn't match what actually goes out.
  const reminderSubjectLine = translate(
    "MC_INQ_DRAFT_SUBJECT_REMINDER_PLACEHOLDER",
  )

  function insertVariable(token: string) {
    const el = bodyRef.current
    const insertion = `${token} `
    const fallbackLen = el?.value.length ?? 0
    const start = el?.selectionStart ?? fallbackLen
    const end = el?.selectionEnd ?? fallbackLen
    const pos = start + insertion.length
    setBody((prev) => prev.slice(0, start) + insertion + prev.slice(end))
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(pos, pos)
    })
  }

  // Variable pills are draggable; take over the drop ourselves instead of
  // letting the browser's native text-drop handle it — natively that inserts
  // the text pre-selected, which we don't want.
  function handleVariableDrop(e: DragEvent<HTMLTextAreaElement>) {
    e.preventDefault()
    const token = e.dataTransfer.getData("text/plain")
    if (token) insertVariable(token)
  }

  function handleVariableDragOver(e: DragEvent<HTMLTextAreaElement>) {
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto border-r border-grey4 bg-grey1">
      <div className="flex flex-col gap-5 px-6 py-5">
        <div className="flex items-center gap-3">
          <McPill tone="attention">
            {translate(
              stage === "reminder"
                ? "MC_INQ_DRAFT_STAGE_REMINDER"
                : "MC_INQ_DRAFT_STAGE_FIRST",
            )}
          </McPill>
        </div>

        {/* recipients */}
        <div className="flex flex-col gap-2">
          <SectionLabel>
            {translate("MC_INQ_DRAFT_SEC_RECIPIENTS")}
          </SectionLabel>
          {bulk ? (
            <RecipientList
              inquiries={inquiries}
              onEmailAdded={onBulkSupplierEmailAdded}
              onRecipientsChanged={onBulkRecipientsChanged}
              pendingAddresses={pendingBulkRecipientAddresses}
            />
          ) : (
            <RecipientEditor
              to={to}
              cc={cc}
              setTo={setTo}
              setCc={setCc}
              supplierId={primary.supplierId}
              supplierName={primary.supplierName}
              supplierNumber={primary.supplierNumber}
            />
          )}
          <SenderAndDefaultCc onOpenSettings={onOpenSettings} />
        </div>

        {/* attachment format + questionnaire language — decided up front, before the
            copy is written, since both are frozen once the email is sent. */}
        {stage !== "reminder" && (
          <AttachmentLanguageSection
            format={format}
            setFormat={setFormat}
            formatLocked={templateSettings?.attachmentFormatLocked ?? false}
            language={language}
            setLanguage={setLanguage}
          />
        )}
        {stage !== "reminder" &&
          articles.length >
            (templateSettings?.scopeWarnSubjects ??
              DEFAULT_SCOPE_WARN_SUBJECTS) && (
            <div
              role="alert"
              className="rounded-md border border-yellow1/30 bg-yellow1/10 px-3 py-2 text-[12px] text-yellow1"
            >
              {translate("MC_INQ_DRAFT_LARGE_SCOPE_NOTE", {
                n: articles.length,
                limit:
                  templateSettings?.scopeWarnSubjects ??
                  DEFAULT_SCOPE_WARN_SUBJECTS,
              })}
            </div>
          )}
        {stage !== "reminder" && (
          <InquiryRegulationsNote
            askedRegulations={regulationDraft.askedRegulations}
            regulationKeys={regulationDraft.regulationKeys}
            regulationScopeError={regulationDraft.regulationScopeError}
            ownPick={regulationDraft.scopeSource === "explicit"}
            editor={regulationEditor}
          />
        )}

        {/* subject */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <SectionLabel>{translate("MC_INQ_DRAFT_SEC_SUBJECT")}</SectionLabel>
            {stage !== "reminder" && (
              <CopyButton value={`${subject} ${refTag}`} />
            )}
          </div>
          {/* #1/#2: composite field (like the settings "inquiry email address") — an
              editable subject with the backend-owned reference tag pinned as a non-editable
              suffix, so the full subject that goes out is visible. On the reminder stage the
              subject is read-only and shown as a neutral "Re: …" placeholder: the backend
              forces "Re: <the exact sent subject>" (which already carries the reference), so
              we drop the separate ref suffix here — it would read as appendable and wouldn't
              match what's sent. */}
          <div className="flex items-stretch overflow-hidden rounded-md border border-grey4 bg-grey2 focus-within:ring-1 focus-within:ring-ring">
            {stage === "reminder" ? (
              <Input
                value={reminderSubjectLine}
                disabled
                className="h-9 rounded-none border-0 bg-transparent text-[13px] shadow-none focus-visible:ring-0 disabled:opacity-100"
              />
            ) : (
              // {{variables}} in yellow, as in the message below.
              <RichTextInput
                value={subject}
                onChange={setSubject}
                className="h-9 min-w-0 flex-1"
                textClassName="px-3 py-2 text-[13px] leading-5"
              />
            )}
            {stage !== "reminder" && (
              <span className="font-mono flex shrink-0 items-center border-l border-grey4 bg-grey3 px-3 text-[12px] text-grey5">
                {refTag}
              </span>
            )}
          </div>
          {stage === "reminder" && (
            <p className="text-[11.5px] text-grey5">
              {translate("MC_INQ_DRAFT_SUBJECT_REMINDER_NOTE")}
            </p>
          )}
        </div>

        {/* body — the org's per-language sign-off (edited via the gear button in the
            page header, next to the language switcher) renders as a READ-ONLY
            continuation of this SAME box, not a separate field: the backend appends it
            to email_body itself on send/reminder (mc_supplier_inquiry_service,
            "Backend-owned, so a customized template cannot drop it"), so it must never
            be typed into `body` — that would send it twice. */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <SectionLabel>{translate("MC_INQ_DRAFT_SEC_BODY")}</SectionLabel>
            <div className="flex items-center gap-3">
              {/* Discoverability link for the box below — a user looking at the
                  signature appended under their draft shouldn't be left wondering
                  where it comes from. Opens the same dialog as the header gear. */}
              <button
                type="button"
                onClick={onEditSignature}
                className="flex items-center gap-1 text-[11px] text-grey5 hover:text-white"
              >
                <Settings className="h-3 w-3" />
                {translate("MC_INQ_DRAFT_EDIT_SIGNATURE")}
              </button>
              {/* Copies what's visually in the box below (body + signature when
                  present) — same "\n\n" join the backend uses to append the signature
                  on send, so what's copied matches what actually goes out. */}
              <CopyButton
                value={signature ? `${body}\n\n${signature}` : body}
              />
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-grey4 bg-grey2 focus-within:border-yellow1">
            <RichTextArea
              ref={bodyRef}
              value={body}
              onChange={setBody}
              onDrop={handleVariableDrop}
              onDragOver={handleVariableDragOver}
              className="rounded-none border-0"
            />
            {signature && (
              <div
                aria-readonly
                className={cn(
                  "border-t border-white/5 px-[14px] py-3 text-[13px] leading-[1.72] whitespace-pre-wrap text-grey5 italic",
                  // Still placeholder text: marked like the field in Settings; the
                  // preview's notice says what is left and opens it.
                  needsWriting(signature) && "border-l-2 border-l-warning",
                )}
              >
                {signature}
              </div>
            )}
          </div>
          {strayTokens.length > 0 && (
            <p role="alert" className="text-[11.5px] text-red1">
              {translate("MC_INQ_DRAFT_STRAY_PLACEHOLDER", {
                tokens: strayTokens.join(", "),
              })}
            </p>
          )}
          <TooltipProvider delayDuration={200}>
            <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-grey5">
              {translate("MC_INQ_DRAFT_VARS_LABEL")}
              {INQUIRY_VARS_BY_LANG[language].map((v) => (
                <Tooltip key={v}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", v)
                        e.dataTransfer.effectAllowed = "copy"
                      }}
                      onClick={() => insertVariable(v)}
                      className="font-mono cursor-grab rounded-full border border-yellow1/20 bg-yellow1/[0.07] px-2 py-0.5 text-[11px] text-yellow1 transition-colors hover:border-yellow1/40 hover:bg-yellow1/12 active:cursor-grabbing"
                    >
                      {v}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {translate(INQUIRY_VAR_TOOLTIP_KEYS[v])}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
        </div>

        {/* Articles + deadline are fixed for a reminder (it re-nudges an existing
            request) — only the subject/body can be edited. */}
        {stage !== "reminder" && (
          <>
            <ArticlesEditor
              bulk={bulk}
              supplierId={primary.supplierId}
              articles={articles}
              setArticles={setArticles}
            />
            <DeadlineDeliverySection
              due={due}
              setDue={setDue}
              onOpenSettings={onOpenSettings}
            />
          </>
        )}
      </div>
    </div>
  )
}
